import {Injectable, NgZone} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {FormFlowEvent, UsageFormFlowTrackerService} from './usage-form-flow-tracker.service';  // form & flow tracking

// Usage stats for the logged-in user, sent to POST /api/user/ui_state (users.user_stats_pages_daily /
// user_stats_events_daily): active time + views per page, and UI events per page (filters used, API errors,
// viewport, filters offered on a page, sections seen / used, dashboard widgets added / removed). Daily aggregates only;
// structure, not content (query-param names, id-stripped paths, slugs of UI labels).
//
// Sections are found automatically, without tagging templates: a titled `.card`, or any element with
// `data-usage-section="key"` (dashboard widgets: `widget:<widgets.id>`). Modals aren't tracked: their titles
// often contain data ("<operator> - Affiliate Managers"), so nothing inside `.modal-content` is.
// Titles are cut before the first ':', ' - ', '#', '(', ' for '… so data in them ("Edit API Key: <name>") isn't kept.
// `data-usage-ignore` turns tracking off for a subtree.
//
// Performance: everything runs outside Angular's zone, so the activity listeners and timers never trigger
// change detection; each listener only writes a timestamp. At most one small request per minute per visible,
// active tab.
//
// An extra layer only: every entry point (start, the listeners and timers, recordApiError, flush) catches its
// own errors, so a tracker bug can never break navigation, error handling or logout.

const TICK_MS = 5000;
// 2 min, not less: people read reports and long tables without touching the mouse (60 s until 2026-10-15)
const IDLE_AFTER_MS = 2 * 60 * 1000;
const FLUSH_MS = 60 * 1000;
// Neutral name on purpose: ad blockers (e.g. uBlock Origin) drop requests whose URL looks like tracking
// (stats, track, beacon, metrics, events, ping…), which would silently lose the data.
const ENDPOINT = '/api/user/ui_state';
// Query params stored with their value (column names / numbers); every other filter by name only
const FILTER_PARAMS_WITH_VALUE = ['sortBy', 'sortOrder', 'perPage'];
// Not tracked: the pages that show these stats (User Insights tab, admin Hexagon usage)
const ANALYSIS_PAGES = ['users/:id/insights', 'admin/usage'];
// Query params that aren't filters: pagination
const IGNORED_PARAMS = ['page'];
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];
// Same cap as MAX_EVENTS in backend/utils/usageStats.js; further distinct events wait for the next flush
const MAX_EVENTS = 300;

const SECTION_SELECTOR = '[data-usage-section], .card';
// Not tracked at all: opt-out subtrees and modals
const IGNORED_AREAS = '[data-usage-ignore], .modal-content';
// Title elements only: a plain heading in a card body is often the record itself ('<h2>{{ operator.name }}</h2>'),
// so a card without one of these is not a section unless it has data-usage-section
const SECTION_TITLE_SELECTOR = '.card-title, legend, .card-header h1, .card-header h2, .card-header h3, .card-header h4';
const SECTION_KEY = /^(widget:\d{1,10}|[a-z_]{1,48})$/;
// A title up to its first separator: 'Edit API Key: x', 'Campaign 12 - x', 'Add new wallet for x'…
const TITLE_CUT = /\s[—–-]\s|[:#("]|\s(?:to|of|for)\s/i;
const SEEN_AFTER_MS = 2000;
// The page's filter form is looked for this long after a page view, and once more after the retry delay
const FILTER_SCAN_MS = 2000;
const FILTER_RETRY_MS = 3000;
// Same as filter names in backend/utils/usageStats.js
const FILTER_NAME = /^[A-Za-z0-9_\-\[\].]{1,64}$/;
// Real filter forms have 5–20 fields; the cap keeps an unusual page from using up a report's MAX_EVENTS
const MAX_FILTERS_PER_PAGE = 30;
const RESCAN_MS = 500;
// Section tracking turns itself off after this many errors; page / time tracking keeps running
const MAX_SECTION_ERRORS = 5;

type UsageEvent = 'filter' | 'filter_available' | 'api_error' | 'viewport' | 'section_seen' | 'section_used' | 'widget_action'
    | FormFlowEvent;
// On / off per tracker, from GET /api/status (backend/config/usage_tracking.js). Missing = on; the backend also
// drops whatever is switched off, so this only saves the browser the work.
export interface UsageTrackingSwitches {
    pages: boolean;
    filters: boolean;
    api_errors: boolean;
    viewport: boolean;
    sections: boolean;
    widgets: boolean;
    forms: boolean;   // form effort (UsageFormFlowTrackerService)
    flows: boolean;   // page-to-page steps (UsageFormFlowTrackerService)
    rejections: boolean;  // what happens after a rejected save (UsageFormFlowTrackerService)
}
type UsageSwitch = keyof UsageTrackingSwitches;
const EVENT_SWITCHES: Record<UsageEvent, UsageSwitch> = {
    filter: 'filters', filter_available: 'filters', api_error: 'api_errors', viewport: 'viewport',
    section_seen: 'sections', section_used: 'sections', widget_action: 'widgets',
    form_save: 'forms', form_field: 'forms', form_abandon: 'forms', page_flow: 'flows', form_rejected: 'rejections',
};

@Injectable({providedIn: 'root'})
export class UsageTrackerService {
    private started = false;
    private switches: UsageTrackingSwitches = {
        pages: true, filters: true, api_errors: true, viewport: true, sections: true, widgets: true, forms: true, flows: true, rejections: true,
    };
    private appBuild = 'local';
    private currentPage: string | null = null;
    private lastActivity = Date.now();
    private lastTick = Date.now();
    private pages = new Map<string, {views: number, seconds: number}>();
    private events = new Map<string, number>();
    // Last query params seen per page, so a filter counts only when it's applied or changed
    private lastParams = new Map<string, Map<string, string>>();
    // Pages whose filter form was already reported in this tab, and the pending look for it
    private filterPagesReported = new Set<string>();
    private filterTimer: ReturnType<typeof setTimeout> | null = null;

    // Section tracking (sections seen / used once per page view)
    private sectionErrors = 0;
    private sectionsOff = false;
    private viewSeq = 0;
    private seenThisView = new Set<string>();
    private usedThisView = new Set<string>();
    private observed = new Set<Element>();
    // Section keys found so far, and cards without a title (forgotten when the page changes: a title can render later)
    private sectionKeys = new WeakMap<Element, string>();
    private untitled = new WeakSet<Element>();
    private seenTimers = new Map<Element, ReturnType<typeof setTimeout>>();
    private seenObserver: IntersectionObserver | null = null;
    private domObserver: MutationObserver | null = null;
    private rescanTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly onUsed = (event: Event) => this.safelySections(() => this.markUsed(event.target));

    constructor(private router: Router, private zone: NgZone,
                private formFlow: UsageFormFlowTrackerService) {}  // form & flow tracking

    // Called once the user is logged in (AppComponent). `appVersion` is the release this tab loaded with,
    // `switches` which trackers run (all of them when unknown).
    start(appVersion?: string | null, switches?: Partial<UsageTrackingSwitches> | null) {
        if (this.started) return;
        this.safely(() => {
            (Object.keys(this.switches) as UsageSwitch[]).forEach(name => {
                if (typeof switches?.[name] === 'boolean') this.switches[name] = switches[name] as boolean;
            });
        });
        // Everything switched off: no listeners, timers or requests at all
        if (!Object.values(this.switches).some(Boolean)) return;
        this.started = true;
        // form & flow tracking: an independent add-on with its own switches and error handling; its events join this report
        this.safely(() => this.formFlow.start({forms: this.switches.forms, flows: this.switches.flows, rejections: this.switches.rejections}, (event, key, page) => this.addEvent(event, key, page)));
        // Outside Angular's zone, like everything else here: the timers set on a page view never trigger change detection
        this.safely(() => this.zone.runOutsideAngular(() => {
            this.appBuild = this.resolveBuild(appVersion);
            this.onNavigation();
        }));
        this.safely(() => this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => this.safely(() => this.zone.runOutsideAngular(() => this.onNavigation()))));

        this.safely(() => this.zone.runOutsideAngular(() => {
            // Mouse / wheel events also reach a browser window that sits behind another app (e.g. an IDE),
            // so they only count while the window has focus
            // Activity listeners and the tick only measure active time (switch 'pages')
            if (this.switches.pages) {
                const markActive = () => this.safely(() => {
                    if (document.hasFocus()) this.lastActivity = Date.now();
                });
                ACTIVITY_EVENTS.forEach(name => document.addEventListener(name, markActive, {passive: true, capture: true}));
                document.addEventListener('scroll', markActive, {passive: true, capture: true});
                setInterval(() => this.safely(() => this.tick()), TICK_MS);
            }

            document.addEventListener('visibilitychange', () => {
                this.lastTick = Date.now();
                if (document.visibilityState === 'hidden') this.flush();
            });
            window.addEventListener('pagehide', () => this.flush());
            // Another app (or browser window) took focus: stop the clock and send what's pending
            window.addEventListener('blur', () => {
                this.lastTick = Date.now();
                this.flush();
            });
            window.addEventListener('focus', () => this.lastTick = Date.now());

            setInterval(() => this.flush(), FLUSH_MS);

            if (this.switches.sections) {
                this.safelySections(() => this.startSectionTracking());
            }
        }));
    }

    // Dashboard: a widget template added to / removed from a dashboard (called after the change is made)
    recordWidgetAction(action: 'add' | 'remove', widgetId: number) {
        if (!this.started || !Number.isInteger(widgetId)) return;
        this.safelySections(() => this.addEvent('widget_action', `${action}:${widgetId}`));
    }

    // Friction: API error responses (from FrontendErrorInterceptor). 0 = aborted / network, 401 = login redirect.
    recordApiError(status: number, method: string, url: string) {
        if (!this.started || !this.currentPage || !status || status === 401) return;
        this.safely(() => {
            const path = new URL(url, location.origin).pathname.replace(/\/\d+(?=\/|$)/g, '/:id').slice(0, 150);
            this.addEvent('api_error', `${status} ${method.toUpperCase()} ${path}`);
        });
    }

    // Sends everything pending (also called before logout)
    flush() {
        this.safely(() => this.flushPending());
    }

    private flushPending() {
        const pages = [...this.pages.entries()]
            .map(([page, stats]) => ({page, views: stats.views, seconds: Math.floor(stats.seconds)}))
            .filter(row => row.views || row.seconds);
        const events = [...this.events.entries()].map(([key, hits]) => {
            const [page, event, ...rest] = key.split('|');
            return {page, event, key: rest.join('|'), hits};
        });
        if (!pages.length && !events.length) return;

        // Keep only the unsent fractions of a second
        this.pages.forEach((stats, page) => {
            const fraction = stats.seconds - Math.floor(stats.seconds);
            if (fraction > 0) this.pages.set(page, {views: 0, seconds: fraction});
            else this.pages.delete(page);
        });
        this.events.clear();

        this.send({app_build: this.appBuild, pages, events});
    }

    private onNavigation() {
        const page = this.pagePattern(this.router.routerState.snapshot.root);
        const previousPage = this.currentPage;
        this.currentPage = page;
        this.safely(() => this.formFlow.onNavigation(previousPage, page));  // form & flow tracking
        this.lastTick = Date.now();
        if (page === null) return;

        if (this.switches.pages) this.pageStats(page).views++;
        if (this.switches.viewport) this.addEvent('viewport', this.viewportBucket());
        this.safelySections(() => this.resetSectionsForView());

        // Filters: count a query param only when it's applied or its value changed since this page was last
        // seen (not on every view while it stays applied, e.g. paging). Names only, except sort / page size.
        const params = this.router.routerState.snapshot.root.queryParamMap;
        const previous = this.lastParams.get(page) || new Map<string, string>();
        const current = new Map<string, string>();
        params.keys.forEach(name => {
            const value = params.getAll(name).join(',');
            if (!value || IGNORED_PARAMS.includes(name)) return;
            current.set(name, value);
            if (previous.get(name) !== value) {
                this.addEvent('filter', FILTER_PARAMS_WITH_VALUE.includes(name) ? `${name}=${value}` : name);
            }
        });
        this.lastParams.set(page, current);
        this.scheduleFilterScan(page);
    }

    // Filters a page offers (switch 'filters'), so unused ones can be listed: its filter form's field names,
    // once per page per tab. Looked for a moment after the page view, once more if not rendered yet.
    private scheduleFilterScan(page: string) {
        if (this.filterTimer) clearTimeout(this.filterTimer);
        this.filterTimer = null;
        if (!this.switches.filters || this.filterPagesReported.has(page)) return;
        const lookAfter = (delay: number, retry: boolean) => {
            this.filterTimer = setTimeout(() => this.safely(() => {
                this.filterTimer = null;
                if (this.currentPage !== page || this.filterPagesReported.has(page)) return;
                if (!this.reportFilterForms(page) && retry) lookAfter(FILTER_RETRY_MS, false);
            }), delay);
        };
        lookAfter(FILTER_SCAN_MS, true);
    }

    // A filter form: a form (outside modals) with a 'search' field, or with a field this page already put in the
    // URL. Field names only (static code names such as 'status'); sorting and paging aren't filters.
    private reportFilterForms(page: string): boolean {
        const inUrl = this.lastParams.get(page) || new Map<string, string>();
        const names = new Set<string>();
        let found = false;
        document.querySelectorAll('form').forEach(form => {
            if (form.closest(IGNORED_AREAS)) return;
            const fields = Array.from(form.querySelectorAll('[formcontrolname]')).map(el => el.getAttribute('formcontrolname') || '');
            if (!fields.includes('search') && !fields.some(name => inUrl.has(name))) return;
            found = true;
            fields.filter(name => FILTER_NAME.test(name) && !IGNORED_PARAMS.includes(name) && !FILTER_PARAMS_WITH_VALUE.includes(name))
                .forEach(name => names.add(name));
        });
        if (!found) return false;
        this.filterPagesReported.add(page);
        [...names].slice(0, MAX_FILTERS_PER_PAGE).forEach(name => this.addEvent('filter_available', name, page));
        return true;
    }

    // Active time: tab visible, browser window focused, and the user did something in the last 2 min.
    // (A window behind another app still reports visibilityState 'visible', hence the focus check.)
    private tick() {
        const now = Date.now();
        const elapsed = Math.min(now - this.lastTick, TICK_MS * 2);  // timers can be throttled; never count a big jump
        this.lastTick = now;
        if (!this.switches.pages || !this.currentPage || document.visibilityState !== 'visible' || !document.hasFocus() || now - this.lastActivity > IDLE_AFTER_MS) return;
        this.pageStats(this.currentPage).seconds += elapsed / 1000;
    }

    // 'users/:id/insights': the configured paths of the active route chain; null for pages we don't track
    private pagePattern(root: ActivatedRouteSnapshot): string | null {
        const parts: string[] = [];
        let route: ActivatedRouteSnapshot | null = root;
        while (route) {
            const path = route.routeConfig?.path;
            if (path) parts.push(path);
            route = route.firstChild;
        }
        const page = parts.join('/');
        // '' = the root route before its redirect to /dashboard has run
        if (page === '' || page === '**' || /^(auth|error_\d+)(\/|$)/.test(page)) return null;
        // The usage stats pages themselves: looking at the stats shouldn't become the stats
        if (ANALYSIS_PAGES.includes(page)) return null;
        return page.slice(0, 191);
    }

    private viewportBucket(): string {
        const width = window.innerWidth;
        if (width < 768) return '<768';
        if (width < 1280) return '768-1279';
        if (width < 1440) return '1280-1439';
        if (width < 1920) return '1440-1919';
        return '1920+';
    }

    // The release this tab runs (from /api/status), else the Sentry release, else the main bundle hash, else 'local'
    private resolveBuild(appVersion?: string | null): string {
        const candidates = [appVersion, environment.SENTRY_RELEASE !== 'unknown' ? environment.SENTRY_RELEASE : null];
        const mainScript = Array.from(document.scripts).map(script => script.src).find(src => /\/main\.[a-f0-9]+\.js$/.test(src));
        if (mainScript) candidates.push(mainScript.match(/main\.([a-f0-9]+)\.js$/)?.[1]);
        const build = candidates.find(candidate => typeof candidate === 'string' && /^[A-Za-z0-9._-]{1,40}$/.test(candidate));
        return build || 'local';
    }

    private pageStats(page: string) {
        let stats = this.pages.get(page);
        if (!stats) {
            stats = {views: 0, seconds: 0};
            this.pages.set(page, stats);
        }
        return stats;
    }

    private addEvent(event: UsageEvent, key: string, page: string | null = this.currentPage) {
        if (page === null || !this.switches[EVENT_SWITCHES[event]]) return;
        const mapKey = `${page}|${event}|${key}`;
        if (!this.events.has(mapKey) && this.events.size >= MAX_EVENTS) return;
        this.events.set(mapKey, (this.events.get(mapKey) || 0) + 1);
    }

    // --- Sections ------------------------------------------------------------------------------------------

    private startSectionTracking() {
        const listen = {passive: true, capture: true};
        ['pointerdown', 'keydown', 'focusin'].forEach(name => document.addEventListener(name, this.onUsed, listen));

        // "Seen" needs both observers; without them only "used" is tracked
        if (typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') return;
        this.seenObserver = new IntersectionObserver(
            entries => this.safelySections(() => entries.forEach(entry => this.onIntersection(entry))),
            {threshold: [0, 0.25, 0.5, 0.75, 1]},
        );
        this.domObserver = new MutationObserver(() => this.safelySections(() => this.scheduleRescan()));
        this.domObserver.observe(document.body, {childList: true, subtree: true});
        this.scheduleRescan();
    }

    private stopSectionTracking() {
        this.sectionsOff = true;
        ['pointerdown', 'keydown', 'focusin'].forEach(name => document.removeEventListener(name, this.onUsed, true));
        this.seenObserver?.disconnect();
        this.domObserver?.disconnect();
        this.seenTimers.forEach(timer => clearTimeout(timer));
        this.seenTimers.clear();
        this.observed.clear();
        if (this.rescanTimer) clearTimeout(this.rescanTimer);
        if (!environment.production) console.warn('UsageTracker: section tracking turned off after repeated errors');
    }

    // New page view: sections count again, and observing again re-reports what is already on screen
    private resetSectionsForView() {
        this.viewSeq++;
        this.seenThisView.clear();
        this.usedThisView.clear();
        this.seenTimers.forEach(timer => clearTimeout(timer));
        this.seenTimers.clear();
        if (!this.seenObserver) return;
        this.seenObserver.disconnect();
        this.observed.clear();
        this.scheduleRescan();
    }

    private scheduleRescan() {
        if (this.rescanTimer || this.sectionsOff) return;
        this.rescanTimer = setTimeout(() => this.safelySections(() => {
            this.rescanTimer = null;
            this.rescan();
        }), RESCAN_MS);
    }

    // Observe sections added since the last scan; forget removed ones (so they can be garbage-collected)
    private rescan() {
        this.untitled = new WeakSet<Element>();
        this.observed.forEach(el => {
            if (el.isConnected) return;
            this.seenObserver?.unobserve(el);
            this.observed.delete(el);
            this.cancelSeenTimer(el);
        });
        document.querySelectorAll(SECTION_SELECTOR).forEach(el => {
            if (this.observed.has(el) || el.closest(IGNORED_AREAS)) return;
            this.observed.add(el);
            this.seenObserver?.observe(el);
        });
    }

    // In view: at least half of it visible, or it fills at least half of the screen (a tall card never gets to 50%)
    private onIntersection(entry: IntersectionObserverEntry) {
        const el = entry.target;
        const inView = entry.isIntersecting
            && (entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight / 2);
        if (!inView) {
            this.cancelSeenTimer(el);
            return;
        }
        if (this.seenTimers.has(el)) return;
        const viewSeq = this.viewSeq;
        this.seenTimers.set(el, setTimeout(() => this.safelySections(() => {
            this.seenTimers.delete(el);
            if (viewSeq !== this.viewSeq || !el.isConnected || document.visibilityState !== 'visible') return;
            const key = this.sectionKey(el);
            if (key && !this.seenThisView.has(key)) {
                this.seenThisView.add(key);
                this.addEvent('section_seen', key);
            }
        }), SEEN_AFTER_MS));
    }

    private cancelSeenTimer(el: Element) {
        const timer = this.seenTimers.get(el);
        if (timer === undefined) return;
        clearTimeout(timer);
        this.seenTimers.delete(el);
    }

    private markUsed(target: EventTarget | null) {
        if (!(target instanceof Element) || target.closest(IGNORED_AREAS)) return;
        const key = this.sectionOf(target);
        if (!key || this.usedThisView.has(key)) return;
        this.usedThisView.add(key);
        this.addEvent('section_used', key);
    }

    // Key of the innermost section around an element (a card without a title belongs to its parent section)
    private sectionOf(el: Element): string | null {
        let section = el.closest(SECTION_SELECTOR);
        while (section) {
            const key = this.sectionKey(section);
            if (key) return key;
            section = section.parentElement?.closest(SECTION_SELECTOR) ?? null;
        }
        return null;
    }

    // data-usage-section, else the card title; null when there's no usable title
    private sectionKey(section: Element): string | null {
        const explicit = section.getAttribute('data-usage-section');
        if (explicit !== null) return SECTION_KEY.test(explicit) ? explicit : null;
        const cached = this.sectionKeys.get(section);
        if (cached) return cached;
        if (this.untitled.has(section)) return null;
        // The first heading that belongs to this section (not to a section nested in it, nor to a table)
        const title = Array.from(section.querySelectorAll(SECTION_TITLE_SELECTOR))
            .find(heading => heading.closest(SECTION_SELECTOR) === section && !heading.closest('table, [data-usage-ignore]'));
        const text = (title?.textContent || '').replace(/\s+/g, ' ').split(TITLE_CUT)[0];
        const slug = this.slug(text, 48);
        if (!slug) {
            this.untitled.add(section);
            return null;
        }
        this.sectionKeys.set(section, slug);
        return slug;
    }

    // 'Brands (12)' -> 'brands': lowercase letters and '_' only (digits dropped, so counts and ids never get in)
    private slug(value: string | null | undefined, max: number): string {
        return (value || '').toLowerCase().replace(/[^a-z]+/g, '_').replace(/^_+/, '').slice(0, max).replace(/_+$/, '');
    }

    // Like safely(), and after repeated errors section tracking switches itself off
    private safelySections(step: () => void) {
        if (this.sectionsOff) return;
        try {
            step();
        } catch (e) {
            if (!environment.production) console.warn('UsageTracker:', e);
            if (++this.sectionErrors >= MAX_SECTION_ERRORS) {
                try {
                    this.stopSectionTracking();
                } catch {
                    // already off
                }
            }
        }
    }

    // Runs a tracker step; an error is dropped (logged in development), never passed on to the app
    private safely(step: () => void) {
        try {
            step();
        } catch (e) {
            if (!environment.production) console.warn('UsageTracker:', e);
        }
    }

    // Fire-and-forget; bypasses BackendApiService on purpose (no 401 redirect / toasts for telemetry).
    // A plain fetch with keepalive (survives the tab closing), not navigator.sendBeacon: ad blockers block
    // beacon-type requests, and sendBeacon reports success even when the request is dropped.
    private send(body: object) {
        fetch(ENDPOINT, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {'Content-Type': 'application/json'},
            credentials: 'same-origin',
            keepalive: true,
        }).catch(() => undefined);
    }
}
